import type { OfferDetailView, OfferTermsRequest } from '@nest-recruit/shared';

// OFR-01's figures as the builder holds them: whole taka (or pounds) as typed, turned into
// minor units on the way out (#7). Pure, so the money arithmetic is tested on its own.

/** Minor units per whole unit — every currency the system pays in has a hundred (money.ts). */
const MINOR_PER_MAJOR = 100n;
/** §L26 salary: at most nine digits. */
const MAX_DIGITS = 9;

export interface DraftTerms {
  readonly base: string;
  readonly allowances: readonly { readonly label: string; readonly amount: string }[];
  readonly joiningDate: string;
  readonly validityDays: number;
  readonly probationMonths: number;
  readonly noticePeriod: string;
  readonly workLocation: string;
  readonly isCurrentManagerTold: boolean;
}

export function draftOf(offer: OfferDetailView): DraftTerms {
  return {
    base: majorOf(offer.offer.baseSalaryMinor),
    allowances: (offer.offer.allowances ?? []).map((line) => ({ label: line.label, amount: majorOf(line.amountMinor) })),
    joiningDate: offer.joiningDate ?? '',
    validityDays: offer.validityDays,
    probationMonths: offer.probationMonths,
    noticePeriod: offer.noticePeriod ?? '',
    workLocation: offer.workLocation ?? '',
    isCurrentManagerTold: offer.isCurrentManagerTold,
  };
}

export function requestOf(terms: DraftTerms, version: number): OfferTermsRequest {
  return {
    version,
    baseSalaryMinor: terms.base === '' ? null : minorOf(terms.base),
    allowances: terms.allowances.filter((line) => line.label.trim() !== '' && line.amount !== '').map((line) => ({ label: line.label.trim(), amountMinor: minorOf(line.amount) })),
    joiningDate: terms.joiningDate === '' ? null : terms.joiningDate,
    validityDays: Math.max(1, terms.validityDays),
    probationMonths: terms.probationMonths,
    noticePeriod: terms.noticePeriod.trim() || null,
    workLocation: terms.workLocation.trim() || null,
    isCurrentManagerTold: terms.isCurrentManagerTold,
  };
}

/** Whole units in, minor units out — an amount is never a float (#7). */
export function minorOf(major: string): string {
  return (BigInt(major) * MINOR_PER_MAJOR).toString();
}

export function majorOf(minor: string | null | undefined): string {
  return minor ? (BigInt(minor) / MINOR_PER_MAJOR).toString() : '';
}

export function grossOf(terms: DraftTerms): bigint | null {
  if (terms.base === '') return null;
  return terms.allowances.reduce((sum, line) => sum + (line.amount === '' ? 0n : BigInt(line.amount) * MINOR_PER_MAJOR), BigInt(terms.base) * MINOR_PER_MAJOR);
}

export function toBig(value: string | null): bigint | null {
  return value === null ? null : BigInt(value);
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, MAX_DIGITS);
}
